/**
 * azureTopologyApi.ts - Schema d architecture Azure.
 *
 * Le dataset est produit par le worker RunAzureScan (etape 6) a partir du meme
 * token ARM que les 67 controles AZ-*, puis stocke dans le container Cosmos
 * `azure-topologies` (1 document par scan). Son contrat est celui de l outil
 * HYBEOS AzureDescriptor, ce qui permet de le rendre avec le gabarit HTML
 * d origine sans transformation (cf. src/lib/azureDiagram.ts).
 */
import { apiFetch } from './apiClient';

/** Ressource normalisee (sous-ensemble utile cote front ; le dataset en contient plus). */
export interface AzureTopologyResource {
  id:        string;
  name:      string;
  type:      string;
  rg?:       string;
  location?: string;
  summary?:  string;
  placement?: { subnetId?: string | null; vnetId?: string | null };
}

/** Dataset complet, passe tel quel au gabarit du schema. */
export interface AzureTopologyDataset {
  meta: {
    subscriptionId:    string;
    subscriptionName:  string;
    tenantId?:         string;
    exported:          string;
    /** true si l abonnement a depasse le plafond de ressources ou le budget temps. */
    truncated?:        boolean;
    /** true si les properties ARM completes ont ete retirees (document trop gros). */
    propertiesStripped?: boolean;
    listedResources?:  number;
    collectSeconds?:   number;
    [k: string]: unknown;
  };
  resourceGroups: unknown[];
  resources:      AzureTopologyResource[];
  subnets:        unknown[];
  edges:          unknown[];
  peerings:       unknown[];
  errors?:        { area: string; target: string; message: string }[];
  [k: string]: unknown;
}

/** Ligne d historique : projection legere renvoyee par la liste (jamais le dataset). */
export interface AzureTopologyRow {
  id:              string;   // = scanId
  generatedAt:     string;
  connectionId:    string;
  subscriptionId?: string;
  /** Compteurs poses par le worker. Absent sur les releves anterieurs a 2026.09. */
  summary?: {
    resources?:      number;
    resourceGroups?: number;
    subnets?:        number;
    edges?:          number;
    truncated?:      boolean;
  };
}

export interface AzureTopologyDoc {
  id:             string;   // = scanId
  clientId:       string;
  connectionId:   string;
  subscriptionId: string;
  generatedAt:    string;
  dataset:        AzureTopologyDataset;
}

/** Resume porte par le scan lui-meme (permet d afficher l etat sans charger le dataset). */
export interface AzureScanTopologySummary {
  status:          'done' | 'error';
  generatedAt?:    string;
  resources?:      number;
  resourceGroups?: number;
  subnets?:        number;
  edges?:          number;
  truncated?:      boolean;
  durationSec?:    number;
  error?:          string;
}

export const azureTopologyApi = {
  /** Dernier schema disponible pour une connexion (null si aucun scan ne l a encore produit). */
  latest: (connectionId?: string) =>
    apiFetch<AzureTopologyDoc | null>(
      `/clients/azure-topologies/latest${connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : ''}`,
    ),

  /** Historique des releves (metadonnees seules, du plus recent au plus ancien). */
  list: (connectionId?: string, limit = 30) =>
    apiFetch<{ topologies: AzureTopologyRow[] }>(
      `/clients/azure-topologies?limit=${limit}${connectionId ? `&connectionId=${encodeURIComponent(connectionId)}` : ''}`,
    ),

  /**
   * Convertit un releve en fichier editable (draw.io ou Visio).
   * Le backend renvoie le binaire en base64 : l enveloppe JSON de l API ne
   * transporte pas de binaire brut.
   */
  export: (scanId: string, format: 'drawio' | 'vsdx', lang: 'FR' | 'EN' = 'FR') =>
    apiFetch<{ fileName: string; contentType: string; bytes: number; base64: string }>(
      `/clients/azure-topologies/${encodeURIComponent(scanId)}/export?format=${format}&lang=${lang}`,
    ),

  /** Schema d un scan precis. 404 si ce scan n en a pas produit. */
  get: (scanId: string) =>
    apiFetch<AzureTopologyDoc>(`/clients/azure-topologies/${encodeURIComponent(scanId)}`),
};
